"use client";

import { useState, useEffect } from "react";
import { Button } from "@/shadcnComponents/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shadcnComponents/ui/dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shadcnComponents/ui/card";
import {
  CheckCircle,
  XCircle,
  Eye,
  Heart,
  MessageCircle,
  Repeat,
} from "lucide-react";
import { dateTimeFormat } from "@/Helpers/times-ago";

// Define the metrics type
type Metrics = {
  impressions: number;
  likes: number;
  comments: number;
  reposts: number;
};

export default function SyncCollabPopup({
  syncHistory,
  isSyncSuccessful,
  handleSync,
}) {
  const [open, setOpen] = useState(true);
  const [syncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState("");
  const [currentSyncTime, setCurrentSyncTime] = useState("");

  // Example metrics - in a real app, these would come from your API
  const [beforeMetrics, setBeforeMetrics] = useState<Metrics>({
    impressions: 0,
    likes: 0,
    comments: 0,
    reposts: 0,
  });

  const [afterMetrics, setAfterMetrics] = useState<Metrics>({
    impressions: 0,
    likes: 0,
    comments: 0,
    reposts: 0,
  });

  useEffect(() => {
    const sortedSyncHistory = syncHistory?.sort(
      (a, b) =>
        new Date(b.timestamp)?.getTime() - new Date(a.timestamp)?.getTime()
    );
    const currentSync = sortedSyncHistory[0];
    const lastSync = sortedSyncHistory[2];

    setBeforeMetrics({
      impressions: lastSync?.metrics?.impressions,
      likes: lastSync?.metrics?.likes,
      comments: lastSync?.metrics?.comments,
      reposts: lastSync?.metrics?.reposts,
    });
    setLastSyncTime(lastSync?.timestamp);
    setAfterMetrics({
      impressions: currentSync?.metrics?.impressions,
      likes: currentSync?.metrics?.likes,
      comments: currentSync?.metrics?.comments,
      reposts: currentSync?.metrics?.reposts,
    });
    setCurrentSyncTime(currentSync?.timestamp);
  }, [syncHistory]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md md:max-w-lg lg:max-w-xl bg-[#1a1a1a] text-white border-gray-800 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-xl font-bold text-center text-white">
            Sync Collab Data
          </DialogTitle>

          {/* Single line subheading for success/failure message */}
          <div className="flex items-center justify-center mt-2">
            {!isSyncSuccessful ? (
              <div className="flex items-center text-[#ff6b6b] gap-2">
                <XCircle className="h-5 w-5" />
                <span className="font-medium text-sm sm:text-base">
                  Collaboration sync unsuccessful. Please try again.
                </span>
              </div>
            ) : (
              <div className="flex items-center text-green-500 gap-2">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium text-sm sm:text-base">
                  Collaboration synced successfully!
                </span>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="py-2">
          {/* Metrics displayed in two horizontal cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              {/* Previous Metrics Card */}
              <Card className="bg-[#252525] border-gray-700 text-white">
                <CardHeader className="pb-2 pt-3">
                  <CardTitle className="text-center text-base text-gray-300">
                    Old stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Eye className="h-5 w-5 text-yellow-500" />
                        <span className="font-medium text-sm sm:text-base">
                          Impressions
                        </span>
                      </div>
                      <span className="text-base sm:text-lg">
                        {beforeMetrics.impressions ?? "-"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Heart className="h-5 w-5 text-red-500" />
                        <span className="font-medium text-sm sm:text-base">
                          Likes
                        </span>
                      </div>
                      <span className="text-base sm:text-lg">
                        {beforeMetrics.likes ?? "-"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="h-5 w-5 text-blue-500" />
                        <span className="font-medium text-sm sm:text-base">
                          Comments
                        </span>
                      </div>
                      <span className="text-base sm:text-lg">
                        {beforeMetrics.comments ?? "-"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Repeat className="h-5 w-5 text-green-500" />
                        <span className="font-medium text-sm sm:text-base">
                          Reposts
                        </span>
                      </div>
                      <span className="text-base sm:text-lg">
                        {beforeMetrics.reposts ?? "-"}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {/* Previous metrics timestamp */}
              <p className="text-xs text-gray-400 w-full text-center">
                {lastSyncTime ? dateTimeFormat(new Date(lastSyncTime)) : ""}
              </p>
            </div>

            <div className="space-y-2">
              {/* After Metrics Card */}
              <Card className="bg-[#252525] border-gray-700 text-white">
                <CardHeader className="pb-2 pt-3">
                  <CardTitle className="text-center text-base text-gray-300">
                    Updated Stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Eye className="h-5 w-5 text-yellow-500" />
                        <span className="font-medium text-sm sm:text-base">
                          Impressions
                        </span>
                      </div>
                      <span className="text-base sm:text-lg">
                        {afterMetrics.impressions}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Heart className="h-5 w-5 text-red-500" />
                        <span className="font-medium text-sm sm:text-base">
                          Likes
                        </span>
                      </div>
                      <span className="text-base sm:text-lg">
                        {afterMetrics.likes}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="h-5 w-5 text-blue-500" />
                        <span className="font-medium text-sm sm:text-base">
                          Comments
                        </span>
                      </div>
                      <span className="text-base sm:text-lg">
                        {afterMetrics.comments}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Repeat className="h-5 w-5 text-green-500" />
                        <span className="font-medium text-sm sm:text-base">
                          Reposts
                        </span>
                      </div>
                      <span className="text-base sm:text-lg">
                        {afterMetrics.reposts ?? "-"}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {/* After metrics timestamp */}
              <p className="text-xs text-gray-400 w-full text-center">
                {currentSyncTime
                  ? dateTimeFormat(new Date(currentSyncTime))
                  : "-"}
              </p>
            </div>
          </div>
        </div>

        {/* Only show Sync button when sync is unsuccessful */}
        {!isSyncSuccessful && (
          <DialogFooter className="pt-4">
            <Button
              onClick={handleSync}
              disabled={syncing}
              className="w-full bg-[#e74c3c] hover:bg-[#c0392b] text-sm sm:text-base py-2 sm:py-5"
            >
              {syncing ? "Syncing..." : "Sync Again"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
